import {
  Divider,
  Icon,
  PlainButton,
  Popover,
  PopoverContent,
  StatusIndicator,
  Text,
  Truncated,
} from '@customerio/pluma-components/react';
import { isSessionRunning } from '@customerio/hydra-copilot';
import type { CopilotSession, CopilotSessionStatus, RegistryEntry } from '@customerio/hydra-copilot';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { animate, m, useMotionValue } from 'motion/react';
import clsx from 'clsx';
import { useIsAgentLauncherSuppressed } from '../../hooks/use-suppress-agent-launcher';
import { useSidebarActive } from '@/features/sidebar';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { useShouldShowAgentBar } from '../../hooks/use-agent-fullscreen';
import { useCopilotSidebar } from '../../hooks/use-copilot-sidebar';
import { useAgentConversations, type AgentConversationRow } from '../../hooks/use-agent-conversations';
import { useCopilotConversations, useCopilotSessionContext } from '../session-provider';
import { AskAgentSparkleIcon } from '../ask-agent-sparkle-icon';
import { useSuggestions } from '../suggestions';
import { formatCompactRelativeTime } from './menu-format';
import { CORNER_POPOVER_PLACEMENT, nearestCorner, useFabCorner } from './use-fab-corner';
import { useInitialReveal } from './use-initial-reveal';
import * as styles from './index.css';

const MAX_MENU_SUGGESTIONS = 3;

/** Live status per registered session, updated on any session's transition. */
function useSessionStatuses(entries: ReadonlyArray<RegistryEntry>) {
  const [statuses, setStatuses] = useState<ReadonlyMap<CopilotSession, CopilotSessionStatus>>(new Map());

  useEffect(() => {
    const recompute = () => {
      setStatuses(new Map(entries.map((entry) => [entry.session, entry.session.__status.get()])));
    };
    recompute();
    const unsubscribes = entries.map((entry) => entry.session.__status.listen(recompute));
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [entries]);

  return statuses;
}

/**
 * Replies that finished (running → ready) and that the user hasn't opened
 * yet. `viewedSession` is the conversation the user is currently reading (in
 * the open copilot sidebar or the fullscreen agent view, null otherwise): its
 * replies arrive read, while a background conversation's reply is unseen even
 * while the launcher itself is hidden.
 * Two levels of acknowledgement: `acknowledgePill` quiets the launcher's
 * announcement (any interaction), `clearUnread` clears a conversation's
 * unread state (opening that conversation).
 */
function useUnseenReplies(entries: ReadonlyArray<RegistryEntry>, viewedSession: CopilotSession | null) {
  // Session → epoch ms of its latest unseen reply.
  const [unreadBySession, setUnreadBySession] = useState<ReadonlyMap<CopilotSession, number>>(new Map());
  const [pillSession, setPillSession] = useState<CopilotSession | null>(null);
  const previousStatuses = useRef(new Map<string, CopilotSessionStatus>());
  const viewedSessionRef = useRef(viewedSession);
  viewedSessionRef.current = viewedSession;

  useEffect(() => {
    // Prune state for sessions the registry evicted, so we don't pin them.
    const liveSessions = new Set<CopilotSession>(entries.map((entry) => entry.session));
    setUnreadBySession((current) => {
      if ([...current.keys()].every((session) => liveSessions.has(session))) return current;
      return new Map([...current].filter(([session]) => liveSessions.has(session)));
    });
    setPillSession((current) => (current != null && !liveSessions.has(current) ? null : current));
    const liveKeys = new Set(entries.map((entry) => entry.key));
    for (const key of [...previousStatuses.current.keys()]) {
      if (!liveKeys.has(key)) previousStatuses.current.delete(key);
    }

    const unsubscribes = entries.map((entry) => {
      if (!previousStatuses.current.has(entry.key)) {
        previousStatuses.current.set(entry.key, entry.session.__status.get());
      }
      return entry.session.__status.listen((status) => {
        const previous = previousStatuses.current.get(entry.key);
        previousStatuses.current.set(entry.key, status);
        if (
          previous != null &&
          isSessionRunning(previous) &&
          status === 'ready' &&
          entry.session !== viewedSessionRef.current
        ) {
          setUnreadBySession((current) => {
            const next = new Map(current);
            next.set(entry.session, Date.now());
            return next;
          });
          setPillSession(entry.session);
        }
      });
    });

    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [entries]);

  const clearUnread = (session: CopilotSession) => {
    setUnreadBySession((current) => {
      if (!current.has(session)) return current;
      const next = new Map(current);
      next.delete(session);
      return next;
    });
    if (pillSession === session) setPillSession(null);
  };

  return { unreadBySession, pillSession, acknowledgePill: () => setPillSession(null), clearUnread };
}

type SectionHeaderProps = { children: React.ReactNode };

function SectionHeader({ children }: SectionHeaderProps) {
  return <div className={styles.sectionHeader}>{children}</div>;
}

const SNAP_SPRING = { type: 'spring', stiffness: 400, damping: 32 } as const;

/**
 * Drag-to-move: free drag via motion, snapping to the nearest content-area
 * corner on release. On a corner change the drag transform is re-based onto
 * the new CSS anchor before paint, so the launcher springs in from exactly
 * where it was dropped.
 */
function useCornerDrag(isHidden: boolean) {
  const { corner, setCorner } = useFabCorner();
  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [isDragging, setIsDragging] = useState(false);
  const didDragRef = useRef(false);
  const droppedRectRef = useRef<DOMRect | null>(null);

  // Hiding unmounts the draggable element without a dragEnd, so any in-flight
  // offset (and drag flag) would reapply stale when the launcher reappears —
  // it must come back parked exactly on its saved corner.
  useEffect(() => {
    if (!isHidden) return;
    x.set(0);
    y.set(0);
    setIsDragging(false);
    didDragRef.current = false;
  }, [isHidden, x, y]);

  const snapToRest = (value: typeof x) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      value.set(0);
      return;
    }
    void animate(value, 0, SNAP_SPRING);
  };

  const handleDragStart = () => {
    didDragRef.current = true;
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    // The gesture's own click (if any) fires before this timeout, so a drag
    // that ends off-button can't swallow the next real click.
    window.setTimeout(() => {
      didDragRef.current = false;
    }, 0);
    const container = containerRef.current;
    const parent = container?.offsetParent;
    if (container == null || parent == null) return;

    const rect = container.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const next = nearestCorner(
      { x: rect.x + rect.width / 2 - parentRect.x, y: rect.y + rect.height / 2 - parentRect.y },
      { width: parentRect.width, height: parentRect.height },
    );

    if (next === corner) {
      snapToRest(x);
      snapToRest(y);
      return;
    }
    droppedRectRef.current = rect;
    setCorner(next);
  };

  useLayoutEffect(() => {
    const dropped = droppedRectRef.current;
    const container = containerRef.current;
    if (dropped == null || container == null) return;
    droppedRectRef.current = null;

    const rect = container.getBoundingClientRect();
    x.set(x.get() + dropped.x - rect.x);
    y.set(y.get() + dropped.y - rect.y);
    snapToRest(x);
    snapToRest(y);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- snapToRest is stable per render; re-run only on corner change
  }, [corner, x, y]);

  /** True during a drag gesture — lets the click handler swallow drag-clicks. */
  const consumeDidDrag = () => didDragRef.current;

  return { corner, containerRef, x, y, isDragging, handleDragStart, handleDragEnd, consumeDidDrag };
}

type ConversationRowProps = {
  row: AgentConversationRow;
  status: CopilotSessionStatus | undefined;
  /** Epoch ms of the row's unseen reply, if any. */
  unreadAt: number | undefined;
  onSelect: (row: AgentConversationRow) => void;
};

function ConversationRow({ row, status, unreadAt, onSelect }: ConversationRowProps) {
  const isRunning = isSessionRunning(status);
  const isBusy = isRunning || status === 'initializing';
  const isUnread = unreadAt != null;
  const label = !isEmptyString(row.name) ? row.name : `Chat ${row.id}`;

  return (
    <PlainButton className={styles.menuRow} onClick={() => onSelect(row)}>
      {isBusy ? (
        <span className={styles.rowIndicator}>
          <StatusIndicator color="green" isPulsing aria-label="The Agent is working" />
        </span>
      ) : isUnread ? (
        <span className={styles.rowIndicator}>
          <span className={styles.unreadDot} role="img" aria-label="New reply" />
        </span>
      ) : null}
      <span className={styles.rowLabel}>
        <Text
          size="sm"
          weight={isBusy || isUnread ? 'semibold' : 'regular'}
          color={isBusy || isUnread ? 'base' : 'subtle'}
          display="block"
          minW="0"
        >
          <Truncated cursor="pointer">{label}</Truncated>
        </Text>
        {isRunning ? (
          <Text size="xs" color="success" display="block">
            Running…
          </Text>
        ) : isUnread ? (
          <Text size="xs" color="subtle" display="block">
            The Agent replied {formatCompactRelativeTime(Math.floor(unreadAt / 1000))}
          </Text>
        ) : null}
      </span>
      <span className={styles.rowStatus}>
        {!isBusy && !isUnread && row.lastActiveAt != null ? (
          <Text size="xs" color="subtle" whiteSpace="nowrap">
            {formatCompactRelativeTime(row.lastActiveAt)}
          </Text>
        ) : null}
      </span>
    </PlainButton>
  );
}

export function AgentFab() {
  const isCopilotSidebarOpen = useSidebarActive('copilot');
  const isHistorySidebarOpen = useSidebarActive('copilot-history');
  const shouldShowAgentBar = useShouldShowAgentBar();
  const isSuppressed = useIsAgentLauncherSuppressed();
  const { track } = useAnalytics();
  const { openCopilot } = useCopilotSidebar();
  const session = useCopilotSessionContext();
  const { newConversation, switchToConversation, entries } = useCopilotConversations();
  const { rows } = useAgentConversations();
  const suggestionState = useSuggestions(session);
  const statuses = useSessionStatuses(entries);
  const isHidden = isCopilotSidebarOpen || isHistorySidebarOpen || !shouldShowAgentBar || isSuppressed;
  // The conversation the user is currently reading: the active session while
  // the copilot sidebar or the fullscreen agent view shows it.
  const viewedSession = isCopilotSidebarOpen || !shouldShowAgentBar ? session : null;
  const { unreadBySession, pillSession, acknowledgePill, clearUnread } = useUnseenReplies(entries, viewedSession);
  const isInitialReveal = useInitialReveal(isHidden);
  const drag = useCornerDrag(isHidden);

  const isAnyRunning = [...statuses.values()].some(isSessionRunning);
  const hasUnseenReply = pillSession != null;
  const finishedName =
    pillSession != null ? (rows.find((row) => row.liveSession === pillSession)?.name ?? 'Reply ready') : null;

  if (isHidden) {
    return null;
  }

  const suggestions = suggestionState.isVisible ? suggestionState.suggestions.slice(0, MAX_MENU_SUGGESTIONS) : [];

  const isRowActive = (row: AgentConversationRow) => {
    if (row.liveSession == null) return false;
    const status = statuses.get(row.liveSession);
    return isSessionRunning(status) || status === 'initializing' || unreadBySession.has(row.liveSession);
  };
  const activeRows = rows.filter(isRowActive);
  const recentRows = rows.filter((row) => !isRowActive(row));

  const handleOpen = () => {
    if (drag.consumeDidDrag()) return;
    acknowledgePill();
    if (session.id != null) {
      // Opening the launcher lands in the active conversation; its reply is read.
      const activeEntry = entries.find((entry) => entry.session.id === session.id);
      if (activeEntry != null) clearUnread(activeEntry.session);
    }
    openCopilot({ triggerSource: 'fab' });
  };

  const handleNewConversation = () => {
    newConversation();
    track('aiAssistant', 'newChatClicked', { 'Click Location': 'fab' });
    openCopilot({ triggerSource: 'fab_new_conversation' });
  };

  const handleSelectConversation = (row: AgentConversationRow) => {
    track('aiAssistant', 'historyItemClicked', { 'Click Location': 'fab' });
    const opened = switchToConversation(row.id);
    clearUnread(opened);
    openCopilot({ triggerSource: 'fab_conversation' });
  };

  const handleSuggestionClick = async (message: string, index: number, label: string, group: string | undefined) => {
    try {
      openCopilot({ triggerSource: 'fab_suggestion' });
      await session.sendMessage(message);
      track('aiAssistant', 'suggestionClicked', {
        Index: index,
        'Click Location': 'fab',
        'Conversation ID': session.id,
        'Suggestion Label': label,
        'Suggestion Group': group ?? 'none',
        'Route Group': suggestionState.routeGroup,
        'Suggestion Source': suggestionState.source,
      });
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  return (
    <m.div
      ref={drag.containerRef}
      className={clsx(styles.fabContainer, styles.fabContainerCorner[drag.corner])}
      data-corner={drag.corner}
      drag
      dragMomentum={false}
      style={{ x: drag.x, y: drag.y }}
      onDragStart={drag.handleDragStart}
      onDragEnd={drag.handleDragEnd}
    >
      <Popover
        // Hover-open is intentional (Popover guidance prefers click): hovering
        // previews the conversation menu, while clicking the launcher goes
        // straight to the sidebar — so click can't also toggle the menu.
        shouldTriggerOnClick={false}
        shouldTriggerOnHover
        shouldTriggerOnFocus
        // Keep focus on the launcher when the menu hover-opens, so the first
        // row doesn't paint a focus ring; Tab still reaches the rows.
        initialFocus={-1}
        isDisabled={drag.isDragging}
        placement={CORNER_POPOVER_PLACEMENT[drag.corner]}
        onOpenChange={(isOpen) => {
          if (isOpen) acknowledgePill();
        }}
      >
        {(context) => (
          <>
            <PlainButton
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              aria-label="Ask the Agent"
              data-unseen-reply={hasUnseenReply ? 'true' : undefined}
              data-working={isAnyRunning ? 'true' : undefined}
              className={clsx(
                styles.fabButton,
                (isInitialReveal || hasUnseenReply) && styles.fabButtonExpanded,
                hasUnseenReply && styles.fabButtonPulsing,
                isAnyRunning && styles.fabButtonWorking,
                drag.isDragging && styles.fabButtonDragging,
              )}
              onClick={handleOpen}
            >
              <AskAgentSparkleIcon variant={isAnyRunning ? 'gradient' : 'inverse'} />
              <span className={clsx(styles.fabLabel, hasUnseenReply && styles.fabLabelPulsing)}>
                {finishedName ?? 'Ask the Agent'}
              </span>
            </PlainButton>
            <PopoverContent>
              <div className={styles.menu}>
                <PlainButton className={styles.menuRow} onClick={handleNewConversation}>
                  <Icon name="add" size="sm" color="base" />
                  <Text size="sm" weight="semibold">
                    New chat
                  </Text>
                </PlainButton>
                {suggestions.length > 0 ? (
                  <>
                    <Divider my="075" />
                    <SectionHeader>Suggested</SectionHeader>
                    {suggestions.map((suggestion, index) => (
                      <PlainButton
                        key={suggestion.label}
                        className={styles.menuRow}
                        onClick={() =>
                          void handleSuggestionClick(suggestion.message, index + 1, suggestion.label, suggestion.group)
                        }
                      >
                        <Icon name="sparkle" size="sm" color="accent" />
                        <span className={styles.rowLabel}>
                          <Text size="sm" display="block" minW="0">
                            <Truncated cursor="pointer">{suggestion.label}</Truncated>
                          </Text>
                        </span>
                      </PlainButton>
                    ))}
                  </>
                ) : null}
                {activeRows.length > 0 ? (
                  <>
                    <Divider my="075" />
                    <SectionHeader>Active now</SectionHeader>
                    {activeRows.map((row) => (
                      <ConversationRow
                        key={row.id}
                        row={row}
                        status={row.liveSession != null ? statuses.get(row.liveSession) : undefined}
                        unreadAt={row.liveSession != null ? unreadBySession.get(row.liveSession) : undefined}
                        onSelect={handleSelectConversation}
                      />
                    ))}
                  </>
                ) : null}
                {recentRows.length > 0 ? (
                  <>
                    <Divider my="075" />
                    <SectionHeader>Recent</SectionHeader>
                    {recentRows.map((row) => (
                      <ConversationRow
                        key={row.id}
                        row={row}
                        status={undefined}
                        unreadAt={undefined}
                        onSelect={handleSelectConversation}
                      />
                    ))}
                  </>
                ) : null}
              </div>
            </PopoverContent>
          </>
        )}
      </Popover>
    </m.div>
  );
}
